import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Divider,
    Button,
} from '@mui/material';
import { Trash2, Edit, Plus } from 'lucide-react';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { Appointment } from '../../../types/agendamentos';

interface AppointmentsListProps {
    selectedDate: dayjs.Dayjs;
    appointments: Appointment[];
    onAddAppointment: () => void;
    onEditAppointment: (appointment: Appointment) => void;
    onDeleteAppointment: (id: string) => void;
}

export default function ListEvents({
    selectedDate,
    appointments,
    onAddAppointment,
    onEditAppointment,
    onDeleteAppointment,
}: AppointmentsListProps) {

    function formatHora(time: string | null) {
        if (time) return time.substring(0, 5);
        return '--:--';
    }

    return (
        <Paper elevation={3} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', maxHeight: '60vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
                    {selectedDate.locale('pt-br').format('DD [de] MMMM [de] YYYY')}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Plus size={18} />}
                    onClick={onAddAppointment}
                    sx={{ textTransform: 'none' }}
                >
                    Adicionar
                </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {appointments.length === 0 ? (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                    }}
                >
                    <Typography variant="body1" sx={{ mb: 1, textAlign: 'center' }}>
                        Nenhum agendamento para este dia
                    </Typography>
                    <Typography variant="body2" color="text.disabled" align='center'>
                        Clique em "Adicionar" para criar um novo
                    </Typography>
                </Box>
            ) : (
                <List sx={{ flex: 1, overflow: 'auto' }}>
                    {appointments.map((appointment, index) => (
                        <Box key={appointment.id_agendamento}>
                            <ListItem
                                sx={{
                                    px: 2,
                                    py: 2,
                                    borderRadius: 1,
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    },
                                }}
                                secondaryAction={
                                    <Box>
                                        <IconButton
                                            edge="end"
                                            onClick={() => onEditAppointment(appointment)}
                                            sx={{ mr: 1 }}
                                        >
                                            <Edit size={18} />
                                        </IconButton>
                                        <IconButton
                                            edge="end"
                                            onClick={() => onDeleteAppointment(String(appointment.id_agendamento))}
                                            color="error"
                                        >
                                            <Trash2 size={18} />
                                        </IconButton>
                                    </Box>
                                }
                            >
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            {formatHora(appointment.hora_inicio)} às {formatHora(appointment.hora_fim)} | {appointment.title}
                                        </Typography>
                                    }
                                    secondary={
                                        appointment.description && (
                                            <Typography variant="body2" color="text.secondary">
                                                {appointment.description}
                                            </Typography>
                                        )
                                    }
                                />
                            </ListItem>
                            {index < appointments.length - 1 && <Divider sx={{ my: 1 }} />}
                        </Box>
                    ))}
                </List>
            )}
        </Paper>
    );
}
